import _ from 'underscore';

import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import * as ast from 'notebook/tables/excess/AST';
import tokenizer, {Token} from 'notebook/tables/excess/Tokenizer';
import {getColumnReferenceChipHTML} from 'pipelines/formula/components/ColumnReferenceChip';
import {
  COLUMN_REFERENCE_AT_START_REGEX,
  COLUMN_REFERENCE_PREFIX,
  COLUMN_REFERENCE_REGEX,
} from 'pipelines/formula/Constants';
import {AnalysisFunction} from 'pipelines/formula/FunctionTypes';
import {analysisFunctions} from 'pipelines/formula/functionUtils';

/**
 * Convert the given user-input formula text to HTML to render in the input.
 *
 * Transforms the user text in the following ways:
 *   1. Converts all column reference strings into chip-like spans
 *   2. Adds HTML escaping to all other parts of the formula that are displayed
 */
export function formulaTextToHTML(formulaText: string, dataFrameSpec: DataFrameSpec): string {
  const systemNameToColumn = Object.fromEntries(
    dataFrameSpec.columns.map((column) => [column.systemName, column])
  );
  const matches = [...formulaText.matchAll(COLUMN_REFERENCE_REGEX)];
  let html = '';
  let previousEndIndex = 0;
  matches.forEach((match) => {
    const startIndex = match.index!;
    const endIndex = match[0].length + startIndex;
    html += _.escape(formulaText.slice(previousEndIndex, startIndex));
    html += getColumnReferenceChipHTML(
      match[0].slice(1, match[0].length - 1), // Strip out start and end quotes from the string
      systemNameToColumn[match.groups!.columnSystemName] ?? null
    );
    previousEndIndex = endIndex;
  });
  html += _.escape(formulaText.slice(previousEndIndex));
  return html;
}

/**
 * Convert html into the original user-input formula text.
 *
 * Does so by:
 *  1. Unescaping the html string
 *  2. Converting chip-like spans back into the original column reference strings, using the data-original-text
 *     attribute.
 */
export function htmlToFormulaText(html: string): string {
  const text = _.unescape(html).replace(/<span.+?data-original-text="(.+?)">.+?<\/span>/g, '"$1"');
  return text.endsWith('<br>') ? text.slice(0, -4) : text;
}

/**
 * Return the length of the formula text, counting column reference strings as length 1.
 *
 * The special treatment of column references is because selection indices treat chip-like spans as having
 * length 1.
 */
export function getFormulaLength(formulaText: string): number {
  return formulaText.replace(COLUMN_REFERENCE_REGEX, ' ').length;
}

/**
 * Return the parts of the text before and after the selected indices.
 *
 * Treats column references as having length 1 when considering where the selection is in the formulaText.
 */
export function partitionFormulaText(
  formulaText: string,
  selectionStart: number,
  selectionEnd: number
): {before: string; after: string} {
  // formulaTextIndex is the real index in formula text, ...
  let formulaTextIndex = 0;
  // while transformedIndex is the index when counting column references as having length 1
  let transformedIndex = 0;

  function processUntilIndex(transformedEndIndex: number): string {
    let result = '';
    while (transformedIndex < transformedEndIndex) {
      const prevFormulaTextIndex = formulaTextIndex;
      const columnReference = formulaText
        .substring(formulaTextIndex, formulaText.length)
        .match(COLUMN_REFERENCE_AT_START_REGEX);
      if (columnReference != null) {
        formulaTextIndex += columnReference[0].length;
      } else {
        formulaTextIndex += 1;
      }
      transformedIndex += 1;
      result += formulaText.substring(prevFormulaTextIndex, formulaTextIndex);
    }
    return result;
  }

  const before = processUntilIndex(selectionStart);
  processUntilIndex(selectionEnd);
  const after = processUntilIndex(getFormulaLength(formulaText));
  return {before, after};
}

/**
 * Traverses tokens to find the "open" function and current parameter index.
 *
 * Adapts the similar notebook function for analysis formulas, which need the following changes:
 *   1. We need to search over a different set of functions
 *   2. We need to specially handle column tokens, as they should be treated as having length 1
 *
 * @param formulaText Full formula text
 * @param caretPosition Character index in formula representing the user's text cursor position
 * @returns function - Spreadsheet function in the outer scope.
 * @returns currentParameterIndex - Index of the current parameter of the function, equals -1 if the user's
 * text cursor is within a function name and it may be greater than the number of allowed arguments for the
 * function.
 * @returns null - if it could not find a valid function.
 */
export function getScopedFunction(
  formulaText: string,
  caretPosition: number
): {function: AnalysisFunction; currentParameterIndex: number} | null {
  const tokens = tokenizer.partialTokenize(formulaText, {relative: false}).tokens;
  if (tokens == null || tokens.length < 2) {
    return null;
  }

  let currentParameterIndex = 0;

  /**
   * Slice tokens list to not include tokens that come after the caretPosition, with special logi to include a
   * left paren token if the user's caret is "inside" a function name, even though the left paren token is
   * past caretPosition.
   * For example, a simple string slice with caretPosition=7 would give us "=SUM(12" or "=SUM(LE", while
   * this code would give us "=SUM(123" and "=SUM(LEFT(" instead, which lets us return the function name
   * that the user's cursor is inside.
   */
  const processedTokens: Array<Token> = []; // Tokens that come before the caret position
  let formulaLength = 0; // Total length of Tokens in processedTokens

  for (const token of tokens) {
    if (formulaLength >= caretPosition) {
      if (
        processedTokens.length > 0 &&
        processedTokens[processedTokens.length - 1].isASTNode() &&
        token.isLeftParen()
      ) {
        // Add left paren token in order to recognize the last node as a function name.
        processedTokens.push(token);
        formulaLength += token.text.length;
        // The caret is inside a function name instead of inside a function call, so there is no "current parameter".
        currentParameterIndex = -1;
      }
      break;
    }

    processedTokens.push(token);
    if (
      token.node instanceof ast.Value &&
      token.node.typedValue.type === 'STRING' &&
      token.node.typedValue.rawValue.startsWith(COLUMN_REFERENCE_PREFIX)
    ) {
      formulaLength += 1;
    } else {
      formulaLength += token.text.length;
    }
  }
  // Keep track of how many parentheses deep we are, since "=SUM(ABS()," should return SUM instead of ABS
  // The "open" function is the "first" (going backwards through procssedTokens) ASTNode where nestingLevel < 0
  let nestingLevel = 0;

  for (let i = processedTokens.length - 1; i >= 0; i--) {
    const token = processedTokens[i];
    if (token.isRightParen()) {
      nestingLevel++;
    } else if (token.isLeftParen()) {
      nestingLevel--;
    } else if (nestingLevel === 0 && token.isComma()) {
      // Since nestingLevel === 0, we are in the open function
      currentParameterIndex++;
    } else if (nestingLevel < 0 && token.isASTNode() && token.node instanceof ast.FunctionOperator) {
      // In proper scope and is AST node
      const func = analysisFunctions.find((func) => func.name === (token.node as ast.FunctionOperator).type);
      return func ? {function: func, currentParameterIndex} : null;
    }
  }
  return null;
}
